/**
 * BlueHMS Theme Configuration
 * A comprehensive design system for consistent styling across the application
 */

export const theme = {
  // Color Palette
  colors: {
    // Primary Colors (Purple/Blue Gradient)
    primary: '#667eea',
    primaryDark: '#5a67d8',
    primaryLight: '#7c8ff3',
    secondary: '#764ba2',
    accent: '#6B46C1',

    // Gradient Arrays
    primaryGradient: ['#667eea', '#764ba2', '#6B46C1'],
    lightGradient: ['#e0e7ff', '#e9d5ff'],
    darkGradient: ['#4338ca', '#5b21b6'],

    // Neutral Colors
    white: '#FFFFFF',
    offWhite: '#F8F9FA',
    lightGray: '#E5E7EB',
    mediumGray: '#9CA3AF',
    darkGray: '#6B7280',
    charcoal: '#2D3748',
    black: '#1A1A1A',

    // Background Colors
    background: '#FFFFFF',
    backgroundSecondary: '#F8F9FA',
    backgroundTertiary: '#F3F4F6',

    // Text Colors
    textPrimary: '#1A237E',
    textSecondary: '#4B5563',
    textTertiary: '#6B7280',
    textDisabled: '#9CA3AF',
    textWhite: '#FFFFFF',

    // Status Colors
    success: '#10B981',
    successLight: '#D1FAE5',
    error: '#DC2626',
    errorLight: '#FEE2E2',
    warning: '#F59E0B',
    warningLight: '#FEF3C7',
    info: '#3B82F6',
    infoLight: '#DBEAFE',

    // Border Colors
    borderLight: '#E5E7EB',
    borderMedium: '#D1D5DB',
    borderDark: '#9CA3AF',

    // Shadow Colors
    shadowLight: 'rgba(0, 0, 0, 0.05)',
    shadowMedium: 'rgba(0, 0, 0, 0.1)',
    shadowDark: 'rgba(0, 0, 0, 0.2)',
    shadowColored: 'rgba(102, 126, 234, 0.3)',
  },

  // Typography
  typography: {
    // Font Families
    fontFamily: {
      regular: 'System',
      medium: 'System',
      bold: 'System',
      light: 'System',
    },

    // Font Sizes
    fontSize: {
      xs: 12,
      sm: 14,
      base: 16,
      lg: 18,
      xl: 20,
      '2xl': 24,
      '3xl': 30,
      '4xl': 32,
      '5xl': 40,
    },

    // Font Weights
    fontWeight: {
      light: '300' as const,
      regular: '400' as const,
      medium: '500' as const,
      semibold: '600' as const,
      bold: '700' as const,
    },

    // Line Heights
    lineHeight: {
      tight: 1.2,
      normal: 1.5,
      relaxed: 1.75,
    },

    // Letter Spacing
    letterSpacing: {
      tight: -0.5,
      normal: 0,
      wide: 0.5,
    },
  },

  // Spacing
  spacing: {
    xs: 4,
    sm: 8,
    md: 12,
    base: 16,
    lg: 20,
    xl: 24,
    '2xl': 32,
    '3xl': 40,
    '4xl': 48,
    '5xl': 64,
  },

  // Border Radius
  borderRadius: {
    none: 0,
    sm: 4,
    md: 8,
    base: 12,
    lg: 16,
    xl: 20,
    '2xl': 24,
    '3xl': 32,
    full: 9999,
  },

  // Shadows
  shadows: {
    sm: {
      shadowColor: '#000',
      shadowOffset: {width: 0, height: 2},
      shadowOpacity: 0.05,
      shadowRadius: 3,
      elevation: 2,
    },
    md: {
      shadowColor: '#000',
      shadowOffset: {width: 0, height: 4},
      shadowOpacity: 0.1,
      shadowRadius: 6,
      elevation: 4,
    },
    lg: {
      shadowColor: '#000',
      shadowOffset: {width: 0, height: 8},
      shadowOpacity: 0.15,
      shadowRadius: 12,
      elevation: 8,
    },
    xl: {
      shadowColor: '#000',
      shadowOffset: {width: 0, height: 16},
      shadowOpacity: 0.2,
      shadowRadius: 24,
      elevation: 16,
    },
    colored: {
      shadowColor: '#667eea',
      shadowOffset: {width: 0, height: 8},
      shadowOpacity: 0.3,
      shadowRadius: 12,
      elevation: 8,
    },
  },

  // Component-Specific Styles
  components: {
    // Headers
    header: {
      fontSize: 32,
      fontWeight: '700',
      color: '#1A237E',
      letterSpacing: -0.5,
    },
    subheader: {
      fontSize: 16,
      color: '#6B7280',
      fontWeight: '400',
    },
  },

  // Breakpoints (for responsive design)
  breakpoints: {
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    '2xl': 1536,
  },

  // Transitions
  transitions: {
    fast: 150,
    normal: 250,
    slow: 350,
  },
};

// Export individual parts for easier imports
export const {colors, typography, spacing, borderRadius, shadows, components} =
  theme;

// Helper function to get component styles
export const getComponentStyle = (
  componentName: 'input' | 'button' | 'card',
) => {
  const baseStyles = {
    input: {
      backgroundColor: colors.backgroundSecondary,
      borderRadius: borderRadius.lg,
      borderWidth: 2,
      borderColor: 'transparent',
      height: 60,
      paddingHorizontal: spacing.lg,
      fontSize: typography.fontSize.base,
      color: colors.charcoal,
      fontWeight: typography.fontWeight.medium,
      ...shadows.md,
    },
    button: {
      borderRadius: borderRadius.lg,
      height: 60,
      paddingHorizontal: spacing['2xl'],
      ...shadows.colored,
    },
    card: {
      backgroundColor: colors.white,
      borderRadius: borderRadius['3xl'],
      padding: spacing['2xl'],
      ...shadows.lg,
    },
  };
  return baseStyles[componentName];
};

// Pre-defined style presets for common use cases
export const stylePresets = {
  // Text Presets
  h1: {
    fontSize: typography.fontSize['4xl'],
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
    letterSpacing: typography.letterSpacing.tight,
  },
  h2: {
    fontSize: typography.fontSize['3xl'],
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
    letterSpacing: typography.letterSpacing.tight,
  },
  h3: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: typography.fontWeight.semibold,
    color: colors.textPrimary,
  },
  body: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.regular,
    color: colors.textSecondary,
    lineHeight: typography.lineHeight.normal,
  },
  caption: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.regular,
    color: colors.textTertiary,
  },

  // Container Presets
  container: {
    padding: spacing.xl,
    backgroundColor: colors.background,
  },
  containerCentered: {
    padding: spacing.xl,
    backgroundColor: colors.background,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Card Presets
  cardStandard: {
    backgroundColor: colors.white,
    borderRadius: borderRadius['3xl'],
    padding: spacing['2xl'],
    ...shadows.lg,
  },
  cardElevated: {
    backgroundColor: colors.white,
    borderRadius: borderRadius['3xl'],
    padding: spacing['2xl'],
    ...shadows.xl,
  },
};

export default theme;

